import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, BarChart2, Users, Map, Activity,
  CreditCard, Truck, Star, Weight, Calendar,
  Download, Printer, RefreshCw, Search, ChevronUp, ChevronDown
} from 'lucide-react';
import apiClient from '../services/api';

// ─────────────────────────────────────────────────────────
// Column definitions for every report type
// ─────────────────────────────────────────────────────────
const REPORT_TYPES = [
  { key: 'consignment_details',  label: 'Consignment Details',  icon: FileText   },
  { key: 'consignment_summary',  label: 'Daily Summary',        icon: BarChart2  },
  { key: 'customer_analysis',    label: 'Customer Analysis',    icon: Users      },
  { key: 'route_analysis',       label: 'Route Analysis',       icon: Map        },
  { key: 'status_report',        label: 'Status Report',        icon: Activity   },
  { key: 'payment_status',       label: 'Payment Status',       icon: CreditCard },
  { key: 'delivery_report',      label: 'Delivery Report',      icon: Truck      },
  { key: 'performance_report',   label: 'Performance',          icon: Star       },
  { key: 'weight_analysis',      label: 'Weight Analysis',      icon: Weight     },
  { key: 'monthly_summary',      label: 'Monthly Summary',      icon: Calendar   },
];

const COLUMNS = {
  consignment_details: [
    { key: 'ConsignmentNo',    label: 'Consignment No',   width: 140 },
    { key: 'ConsignmentDate',  label: 'Date',             width: 100, fmt: 'date' },
    { key: 'ConsignmentType',  label: 'Type',             width: 100 },
    { key: 'ShipperCode',      label: 'Shipper Code',     width: 100 },
    { key: 'ShipperName',      label: 'Shipper',          width: 160 },
    { key: 'ConsigneeCode',    label: 'Consignee Code',   width: 110 },
    { key: 'ConsigneeName',    label: 'Consignee',        width: 160 },
    { key: 'OriginCity',       label: 'Origin',           width: 110 },
    { key: 'DestinationCity',  label: 'Destination',      width: 120 },
    { key: 'DriverName',       label: 'Driver',           width: 130, fmt: 'driver' },
    { key: 'Pieces',           label: 'Pcs',              width: 55,  align: 'right' },
    { key: 'Weight',           label: 'Weight',           width: 80,  align: 'right', fmt: 'num3' },
    { key: 'InvoiceValue',     label: 'Value',            width: 90,  align: 'right', fmt: 'num2' },
    { key: 'InvoiceNo',        label: 'Invoice No',       width: 120, fmt: 'invoice' },
    { key: 'Status',           label: 'Status',           width: 110, fmt: 'status' },
    { key: 'DeliveryDate',     label: 'Delivery Date',    width: 110, fmt: 'date' },
    { key: 'DaysInTransit',    label: 'Days',             width: 60,  align: 'right', fmt: 'days' },
    { key: 'Remarks',          label: 'Remarks',          width: 160 },
  ],
  consignment_summary: [
    { key: 'ConsignmentDate',  label: 'Date',             width: 110, fmt: 'date' },
    { key: 'TotalConsignments',label: 'Total',            width: 80,  align: 'right' },
    { key: 'TotalValue',       label: 'Total Value',      width: 110, align: 'right', fmt: 'num2' },
    { key: 'TotalWeight',      label: 'Total Wt',         width: 100, align: 'right', fmt: 'num3' },
    { key: 'TotalPieces',      label: 'Pieces',           width: 75,  align: 'right' },
    { key: 'DeliveredCount',   label: 'Delivered',        width: 85,  align: 'right', fmt: 'green' },
    { key: 'InTransitCount',   label: 'In Transit',       width: 85,  align: 'right', fmt: 'orange' },
    { key: 'PendingCount',     label: 'Pending',          width: 80,  align: 'right', fmt: 'red' },
    { key: 'AvgDeliveryDays',  label: 'Avg Days',         width: 85,  align: 'right', fmt: 'num1' },
  ],
  customer_analysis: [
    { key: 'CustomerCode',     label: 'Code',             width: 100 },
    { key: 'CustomerName',     label: 'Customer',         width: 200 },
    { key: 'TotalConsignments',label: 'Total',            width: 75,  align: 'right' },
    { key: 'TotalValue',       label: 'Total Value',      width: 110, align: 'right', fmt: 'num2' },
    { key: 'TotalWeight',      label: 'Total Wt',         width: 100, align: 'right', fmt: 'num3' },
    { key: 'DeliveredCount',   label: 'Delivered',        width: 85,  align: 'right', fmt: 'green' },
    { key: 'PendingCount',     label: 'Pending',          width: 80,  align: 'right', fmt: 'red' },
    { key: 'FirstConsignment', label: 'First Date',       width: 100, fmt: 'date' },
    { key: 'LastConsignment',  label: 'Last Date',        width: 100, fmt: 'date' },
  ],
  route_analysis: [
    { key: 'OriginCity',       label: 'Origin',           width: 140 },
    { key: 'DestinationCity',  label: 'Destination',      width: 140 },
    { key: 'TotalConsignments',label: 'Total',            width: 75,  align: 'right' },
    { key: 'TotalValue',       label: 'Total Value',      width: 110, align: 'right', fmt: 'num2' },
    { key: 'TotalWeight',      label: 'Total Wt',         width: 100, align: 'right', fmt: 'num3' },
    { key: 'DeliveredCount',   label: 'Delivered',        width: 85,  align: 'right', fmt: 'green' },
    { key: 'AvgDeliveryDays',  label: 'Avg Days',         width: 85,  align: 'right', fmt: 'num1' },
    { key: 'DeliveryRate',     label: 'Del. Rate %',      width: 95,  align: 'right', fmt: 'pct' },
  ],
  status_report: [
    { key: 'Status',           label: 'Status',           width: 140, fmt: 'status' },
    { key: 'TotalConsignments',label: 'Count',            width: 80,  align: 'right' },
    { key: 'Percentage',       label: '%',                width: 75,  align: 'right', fmt: 'pct' },
    { key: 'TotalValue',       label: 'Total Value',      width: 120, align: 'right', fmt: 'num2' },
    { key: 'TotalWeight',      label: 'Total Wt',         width: 100, align: 'right', fmt: 'num3' },
    { key: 'EarliestDate',     label: 'Earliest',         width: 100, fmt: 'date' },
    { key: 'LatestDate',       label: 'Latest',           width: 100, fmt: 'date' },
  ],
  payment_status: [
    { key: 'ConsignmentNo',    label: 'Consignment No',   width: 140 },
    { key: 'ConsignmentDate',  label: 'Date',             width: 100, fmt: 'date' },
    { key: 'ShipperName',      label: 'Shipper',          width: 180 },
    { key: 'InvoiceValue',     label: 'Con. Value',       width: 110, align: 'right', fmt: 'num2' },
    { key: 'InvoicedAmount',   label: 'Invoiced',         width: 100, align: 'right', fmt: 'num2' },
    { key: 'PaymentStatus',    label: 'Pay. Status',      width: 110, fmt: 'payStatus' },
    { key: 'InvoiceNo',        label: 'Invoice No',       width: 120 },
    { key: 'InvoiceDate',      label: 'Invoice Date',     width: 110, fmt: 'date' },
    { key: 'ConsignmentStatus',label: 'Con. Status',      width: 110, fmt: 'status' },
  ],
  delivery_report: [
    { key: 'ConsignmentNo',    label: 'Consignment No',   width: 140 },
    { key: 'ConsignmentDate',  label: 'Date',             width: 100, fmt: 'date' },
    { key: 'ConsigneeName',    label: 'Consignee',        width: 180 },
    { key: 'DestinationCity',  label: 'Destination',      width: 120 },
    { key: 'DeliveryDate',     label: 'Delivery Date',    width: 110, fmt: 'date' },
    { key: 'DaysInTransit',    label: 'Days',             width: 60,  align: 'right', fmt: 'days' },
    { key: 'DeliveryStatus',   label: 'Status',           width: 110, fmt: 'deliveryStatus' },
    { key: 'DeliveredBy',      label: 'Delivered By',     width: 130 },
    { key: 'Weight',           label: 'Weight',           width: 80,  align: 'right', fmt: 'num3' },
    { key: 'Pieces',           label: 'Pcs',              width: 55,  align: 'right' },
    { key: 'PODRemarks',       label: 'POD Remarks',      width: 180 },
  ],
  performance_report: [
    { key: 'EmployeeName',     label: 'Employee/Driver',  width: 180 },
    { key: 'TotalConsignments',label: 'Total',            width: 75,  align: 'right' },
    { key: 'DeliveredCount',   label: 'Delivered',        width: 85,  align: 'right', fmt: 'green' },
    { key: 'DeliveryRate',     label: 'Del. Rate %',      width: 95,  align: 'right', fmt: 'pct' },
    { key: 'AvgDeliveryDays',  label: 'Avg Days',         width: 85,  align: 'right', fmt: 'num1' },
    { key: 'TotalValue',       label: 'Total Value',      width: 120, align: 'right', fmt: 'num2' },
    { key: 'TotalWeight',      label: 'Total Wt',         width: 100, align: 'right', fmt: 'num3' },
  ],
  weight_analysis: [
    { key: 'WeightCategory',   label: 'Weight Category',  width: 140 },
    { key: 'TotalConsignments',label: 'Count',            width: 75,  align: 'right' },
    { key: 'TotalWeight',      label: 'Total Wt',         width: 100, align: 'right', fmt: 'num3' },
    { key: 'AvgWeight',        label: 'Avg Wt',           width: 85,  align: 'right', fmt: 'num3' },
    { key: 'TotalValue',       label: 'Total Value',      width: 120, align: 'right', fmt: 'num2' },
    { key: 'AvgValue',         label: 'Avg Value',        width: 100, align: 'right', fmt: 'num2' },
    { key: 'DeliveredCount',   label: 'Delivered',        width: 85,  align: 'right', fmt: 'green' },
    { key: 'DeliveryRate',     label: 'Del. Rate %',      width: 95,  align: 'right', fmt: 'pct' },
  ],
  monthly_summary: [
    { key: 'MonthYear',        label: 'Month',            width: 140 },
    { key: 'TotalConsignments',label: 'Total',            width: 80,  align: 'right' },
    { key: 'TotalValue',       label: 'Total Value',      width: 120, align: 'right', fmt: 'num2' },
    { key: 'TotalWeight',      label: 'Total Wt',         width: 100, align: 'right', fmt: 'num3' },
    { key: 'DeliveredCount',   label: 'Delivered',        width: 85,  align: 'right', fmt: 'green' },
    { key: 'PendingCount',     label: 'Pending',          width: 80,  align: 'right', fmt: 'red' },
    { key: 'UniqueShippers',   label: 'Shippers',         width: 80,  align: 'right' },
    { key: 'UniqueConsignees', label: 'Consignees',       width: 90,  align: 'right' },
    { key: 'AvgDeliveryDays',  label: 'Avg Days',         width: 85,  align: 'right', fmt: 'num1' },
  ],
};

// ─────────────────────────────────────────────────────────
// Cell formatters
// ─────────────────────────────────────────────────────────
function formatDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function fmt2(v) { return v != null && v !== '' ? parseFloat(v).toFixed(2) : '-'; }
function fmt3(v) { return v != null && v !== '' ? parseFloat(v).toFixed(3) : '-'; }
function fmt1(v) { return v != null && v !== '' ? parseFloat(v).toFixed(1) : '-'; }
function fmtPct(v) { return v != null && v !== '' ? parseFloat(v).toFixed(1) + '%' : '-'; }

const STATUS_COLORS = {
  'Delivered':   { color: '#15803d', fontWeight: '700' },
  'In Transit':  { color: '#d97706', fontWeight: '600' },
  'Overdue':     { color: '#dc2626', fontWeight: '700' },
  'Pending':     { color: '#7c3aed', fontWeight: '500' },
  'In Progress': { color: '#d97706', fontWeight: '500' },
  'Returned':    { color: '#6b7280', fontWeight: '500' },
  'Cancelled':   { color: '#ef4444', fontWeight: '500' },
};

function CellValue({ col, value, row }) {
  const v = value;
  switch (col.fmt) {
    case 'date':   return <span>{formatDate(v)}</span>;
    case 'num2':   return <span>{fmt2(v)}</span>;
    case 'num3':   return <span>{fmt3(v)}</span>;
    case 'num1':   return <span>{fmt1(v)}</span>;
    case 'pct':    return <span>{fmtPct(v)}</span>;
    case 'days': {
      const n = parseInt(v);
      const style = n > 14 ? { color: '#dc2626', fontWeight: '700' }
                  : n > 7  ? { color: '#d97706', fontWeight: '600' }
                  : {};
      return <span style={style}>{n >= 0 ? n : '-'}</span>;
    }
    case 'status': {
      const s = String(v || '');
      return <span style={STATUS_COLORS[s] || {}}>{s || '-'}</span>;
    }
    case 'deliveryStatus': {
      const s = String(v || '');
      return <span style={STATUS_COLORS[s] || {}}>{s || '-'}</span>;
    }
    case 'payStatus': {
      const s = String(v || '');
      const c = s === 'Invoiced' ? '#15803d' : s === 'Partial' ? '#d97706' : '#dc2626';
      return <span style={{ color: c, fontWeight: '600' }}>{s || '-'}</span>;
    }
    case 'invoice': {
      const s = String(v || '');
      if (!s || s === 'Not Invoiced')
        return <span style={{ color: '#dc2626', fontStyle: 'italic' }}>Not Invoiced</span>;
      return <span style={{ color: '#15803d', fontWeight: '600' }}>{s}</span>;
    }
    case 'driver': {
      const s = String(v || '');
      if (!s || s === 'Not Assigned')
        return <span style={{ color: '#dc2626', fontStyle: 'italic' }}>Not Assigned</span>;
      return <span>{s}</span>;
    }
    case 'green': return <span style={{ color: '#15803d', fontWeight: '600' }}>{v ?? '-'}</span>;
    case 'orange':return <span style={{ color: '#d97706', fontWeight: '600' }}>{v ?? '-'}</span>;
    case 'red':   return <span style={{ color: '#dc2626', fontWeight: '600' }}>{v ?? '-'}</span>;
    default:      return <span>{v ?? '-'}</span>;
  }
}

// ─────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────
const ConsignmentReports = () => {
  const today     = new Date().toISOString().slice(0, 10);
  const monthStart= today.slice(0, 8) + '01';

  const [reportType,   setReportType]   = useState('consignment_details');
  const [startDate,    setStartDate]    = useState(monthStart);
  const [endDate,      setEndDate]      = useState(today);
  const [customerID,   setCustomerID]   = useState('');
  const [originID,     setOriginID]     = useState('');
  const [destID,       setDestID]       = useState('');
  const statusFilter = 'All Status';
  const [conType,      setConType]      = useState('');
  const [searchTerm,   setSearchTerm]   = useState('');

  const [customers,    setCustomers]    = useState([]);
  const [cities,       setCities]       = useState([]);
  const [rows,         setRows]         = useState([]);
  const [summary,      setSummary]      = useState({});
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');
  const [hasLoaded,    setHasLoaded]    = useState(false);

  const [sortKey,      setSortKey]      = useState('');
  const [sortDir,      setSortDir]      = useState('asc');

  // Load dropdowns on mount
  useEffect(() => {
    apiClient.get('/ConsignmentReports.php?action=dropdowns')
      .then(res => {
        if (res.data.success) {
          setCustomers(res.data.data.customers || []);
          setCities(res.data.data.cities || []);
        }
      })
      .catch(err => console.error('Dropdown load error:', err));
  }, []);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    setHasLoaded(false);
    setSortKey('');

    const params = new URLSearchParams({
      report_type:       reportType,
      start_date:        startDate,
      end_date:          endDate,
      customer_id:       customerID,
      origin_city_id:    originID,
      dest_city_id:      destID,
      status:            statusFilter,
      consignment_type:  conType,
    });

    try {
      const res = await apiClient.get(`/ConsignmentReports.php?${params}`);
      if (res.data.success) {
        setRows(res.data.data.rows || []);
        setSummary(res.data.data.summary || {});
        setHasLoaded(true);
      } else {
        setError(res.data.message || 'Failed to load report');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error loading report');
    } finally {
      setLoading(false);
    }
  }, [reportType, startDate, endDate, customerID, originID, destID, statusFilter, conType]);

  // Client-side search + sort
  const displayRows = (() => {
    let r = rows;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      r = r.filter(row =>
        Object.values(row).some(v => String(v ?? '').toLowerCase().includes(q))
      );
    }
    if (sortKey) {
      r = [...r].sort((a, b) => {
        const av = a[sortKey] ?? '', bv = b[sortKey] ?? '';
        const an = parseFloat(av), bn = parseFloat(bv);
        const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return r;
  })();

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  // ── CSV Export ──────────────────────────────────────────
  const exportCSV = () => {
    const cols = COLUMNS[reportType] || [];
    const header = cols.map(c => c.label).join(',');
    const body = displayRows.map(row =>
      cols.map(c => {
        let v = row[c.key] ?? '';
        if (c.fmt === 'date') v = formatDate(v);
        return `"${String(v).replace(/"/g, '""')}"`;
      }).join(',')
    ).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `${reportType}_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Print ───────────────────────────────────────────────
  const handlePrint = () => {
    const cols = COLUMNS[reportType] || [];
    const rt   = REPORT_TYPES.find(r => r.key === reportType);
    const win  = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head>
      <title>${rt?.label || 'Report'}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11px; margin: 16px; }
        h2   { margin: 0 0 4px; font-size: 16px; }
        p.sub{ margin: 0 0 10px; color:#555; font-size:11px; }
        table{ border-collapse:collapse; width:100%; }
        th   { background:#667eea; color:#fff; padding:6px 8px; text-align:left; font-size:10px; }
        td   { padding:5px 8px; border-bottom:1px solid #e5e7eb; }
        tr:nth-child(even) td { background:#f9fafb; }
        @media print { @page{ margin:10mm; } }
      </style></head><body>
      <h2>${rt?.label || 'Consignment Report'}</h2>
      <p class="sub">Period: ${startDate} to ${endDate} &nbsp;|&nbsp; Records: ${displayRows.length}</p>
      <table>
        <thead><tr>${cols.map(c => `<th>${c.label}</th>`).join('')}</tr></thead>
        <tbody>
          ${displayRows.map(row =>
            `<tr>${cols.map(c => {
              let v = row[c.key] ?? '';
              if (c.fmt === 'date') v = formatDate(v);
              else if (c.fmt === 'num2') v = fmt2(v);
              else if (c.fmt === 'num3') v = fmt3(v);
              else if (c.fmt === 'num1') v = fmt1(v);
              else if (c.fmt === 'pct')  v = fmtPct(v);
              const align = c.align === 'right' ? ' style="text-align:right"' : '';
              return `<td${align}>${v}</td>`;
            }).join('')}</tr>`
          ).join('')}
        </tbody>
      </table>
      <script>window.onload=()=>{ window.print(); }</script>
      </body></html>`);
    win.document.close();
  };

  const cols = COLUMNS[reportType] || [];

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: '100%', background: '#f9fafb',
    }}>

        {/* ── Header ────────────────────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '24px 28px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: '4px solid #5a67d8', flexShrink: 0,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div style={{
              width: '52px', height: '52px',
              background: 'rgba(255,255,255,0.25)', borderRadius: '14px',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <BarChart2 size={26} color="#ffffff" />
            </div>
            <div>
              <div style={{
                color: '#fff', fontSize: '1.7rem', fontWeight: '800',
                textShadow: '0 2px 6px rgba(0,0,0,0.5)'
              }}>
                Consignment Reports
              </div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem', marginTop: '2px' }}>
                {REPORT_TYPES.find(r => r.key === reportType)?.label}
                {hasLoaded && ` · ${displayRows.length} records`}
              </div>
            </div>
          </div>

        </div>

        {/* ── Report Type Tabs ───────────────────────────────────── */}
        <div style={{
          display: 'flex', overflowX: 'auto', gap: '4px',
          padding: '12px 16px', background: '#f3f4f6',
          borderBottom: '1px solid #e5e7eb', flexShrink: 0
        }}>
          {REPORT_TYPES.map(rt => {
            const active = rt.key === reportType;
            const Icon   = rt.icon;
            return (
              <button
                key={rt.key}
                onClick={() => { setReportType(rt.key); setHasLoaded(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '7px 14px', borderRadius: '8px', border: 'none',
                  cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.82rem',
                  fontWeight: active ? '700' : '500',
                  background: active ? 'linear-gradient(135deg,#667eea,#764ba2)' : 'white',
                  color: active ? '#fff' : '#374151',
                  boxShadow: active ? '0 2px 8px rgba(102,126,234,0.4)' : '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all .15s'
                }}
              >
                <Icon size={13} />
                {rt.label}
              </button>
            );
          })}
        </div>

        {/* ── Filters ────────────────────────────────────────────── */}
        <div style={{
          padding: '14px 20px', background: '#fff',
          borderBottom: '1px solid #e5e7eb', flexShrink: 0
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '10px', alignItems: 'end'
          }}>
            <div>
              <label style={lbl}>From Date</label>
              <input type="date" value={startDate}
                onChange={e => setStartDate(e.target.value)} style={inp} />
            </div>
            <div>
              <label style={lbl}>To Date</label>
              <input type="date" value={endDate}
                onChange={e => setEndDate(e.target.value)} style={inp} />
            </div>
            <div>
              <label style={lbl}>Customer</label>
              <select value={customerID} onChange={e => setCustomerID(e.target.value)} style={inp}>
                <option value="">All Customers</option>
                {customers.map(c => (
                  <option key={c.customerid} value={c.customerid}>
                    {c.customercode} – {c.customername}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={lbl}>Origin City</label>
              <select value={originID} onChange={e => setOriginID(e.target.value)} style={inp}>
                <option value="">All Cities</option>
                {cities.map(c => (
                  <option key={c.cityid} value={c.cityid}>{c.cityname}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={lbl}>Destination City</label>
              <select value={destID} onChange={e => setDestID(e.target.value)} style={inp}>
                <option value="">All Cities</option>
                {cities.map(c => (
                  <option key={c.cityid} value={c.cityid}>{c.cityname}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={lbl}>Con. Type</label>
              <select value={conType} onChange={e => setConType(e.target.value)} style={inp}>
                <option value="">All Types</option>
                <option value="LT">Land Transport</option>
                <option value="D">Domestic</option>
                <option value="A">Air</option>
                <option value="S">Sea</option>
              </select>
            </div>
            <div>
              <label style={lbl}>&nbsp;</label>
              <button onClick={loadReport} disabled={loading} style={{
                width: '100%', padding: '9px 14px', borderRadius: '7px',
                border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? '#9ca3af' : 'linear-gradient(135deg,#667eea,#764ba2)',
                color: '#fff', fontWeight: '700', fontSize: '0.9rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
              }}>
                {loading
                  ? <><div style={spinner} />Loading...</>
                  : <><RefreshCw size={14} />Load Report</>
                }
              </button>
            </div>
          </div>
        </div>

        {/* ── Summary bar + actions ─────────────────────────────── */}
        {hasLoaded && (
          <div style={{
            padding: '10px 20px', background: '#f8fafc',
            borderBottom: '1px solid #e5e7eb', flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap'
          }}>
            <StatBadge label="Records" value={displayRows.length} color="#667eea" />
            {summary.total_value > 0 &&
              <StatBadge label="Total Value" value={parseFloat(summary.total_value).toFixed(2)} color="#10b981" />}
            {summary.total_weight > 0 &&
              <StatBadge label="Total Weight" value={parseFloat(summary.total_weight).toFixed(3) + ' kg'} color="#f59e0b" />}
            {summary.delivered_count > 0 &&
              <StatBadge label="Delivered" value={summary.delivered_count} color="#15803d" />}

            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              {/* Search */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '7px 10px', border: '1px solid #d1d5db',
                borderRadius: '7px', background: '#fff'
              }}>
                <Search size={14} color="#9ca3af" />
                <input
                  type="text" placeholder="Search…"
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  style={{ border: 'none', outline: 'none', width: '140px', fontSize: '0.85rem' }}
                />
              </div>
              <button onClick={exportCSV} style={actionBtn('#374151')}>
                <Download size={15} /> CSV
              </button>
              <button onClick={handlePrint} style={actionBtn('#374151')}>
                <Printer size={15} /> Print
              </button>
            </div>
          </div>
        )}

        {/* ── Error ─────────────────────────────────────────────── */}
        {error && (
          <div style={{
            margin: '12px 20px', padding: '10px 14px', borderRadius: '8px',
            background: '#fee2e2', border: '1px solid #fca5a5',
            color: '#dc2626', fontSize: '0.9rem', flexShrink: 0
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* ── Table ─────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {!hasLoaded && !loading && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
              <BarChart2 size={48} style={{ margin: '0 auto 12px', opacity: .4 }} />
              <p style={{ fontSize: '1rem', margin: 0 }}>
                Select filters and click <strong>Load Report</strong>
              </p>
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '60px', color: '#667eea' }}>
              <div style={{ ...spinner, margin: '0 auto 12px', width: '32px', height: '32px', borderWidth: '3px' }} />
              <p style={{ margin: 0 }}>Loading report…</p>
            </div>
          )}

          {hasLoaded && !loading && (
            <>
              {displayRows.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>
                  <FileText size={40} style={{ margin: '0 auto 10px', opacity: .4 }} />
                  <p style={{ margin: 0 }}>No data found for the selected filters.</p>
                </div>
              ) : (
                <table style={{
                  width: '100%', borderCollapse: 'collapse',
                  fontSize: '0.82rem', minWidth: '800px'
                }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', position: 'sticky', top: 0, zIndex: 2 }}>
                      {cols.map(c => (
                        <th
                          key={c.key}
                          onClick={() => handleSort(c.key)}
                          style={{
                            padding: '9px 10px',
                            textAlign: c.align || 'left',
                            fontWeight: '700', fontSize: '0.78rem',
                            color: '#374151', cursor: 'pointer',
                            whiteSpace: 'nowrap', userSelect: 'none',
                            borderBottom: '2px solid #e5e7eb',
                            borderRight: '1px solid #e5e7eb',
                            minWidth: c.width,
                          }}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                            {c.label}
                            {sortKey === c.key
                              ? sortDir === 'asc'
                                ? <ChevronUp size={12} color="#667eea" />
                                : <ChevronDown size={12} color="#667eea" />
                              : <ChevronUp size={12} color="#d1d5db" />}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((row, i) => {
                      const isInvoiced = row.IsInvoiced === 1 || row.IsInvoiced === '1';
                      return (
                        <tr
                          key={i}
                          style={{
                            background: isInvoiced ? '#f0fdf4' : i % 2 === 0 ? '#fff' : '#f9fafb',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                          onMouseLeave={e => e.currentTarget.style.background =
                            isInvoiced ? '#f0fdf4' : i % 2 === 0 ? '#fff' : '#f9fafb'}
                        >
                          {cols.map(c => (
                            <td
                              key={c.key}
                              style={{
                                padding: '7px 10px',
                                borderBottom: '1px solid #e5e7eb',
                                borderRight: '1px solid #f1f5f9',
                                textAlign: c.align || 'left',
                                whiteSpace: c.width > 140 ? 'normal' : 'nowrap',
                                fontFamily: c.align === 'right' ? '"Courier New", monospace' : 'inherit',
                              }}
                            >
                              <CellValue col={c} value={row[c.key]} row={row} />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                  {/* Totals footer for numeric columns */}
                  {displayRows.length > 1 && (
                    <tfoot>
                      <tr style={{ background: '#f1f5f9', fontWeight: '700' }}>
                        {cols.map((c, ci) => {
                          if (ci === 0) return (
                            <td key={c.key} style={tdFoot}>
                              TOTAL ({displayRows.length})
                            </td>
                          );
                          const numFmts = ['num2','num3','num1','green','orange','red'];
                          if (numFmts.includes(c.fmt) || c.align === 'right') {
                            const sum = displayRows.reduce((s, r) => s + (parseFloat(r[c.key]) || 0), 0);
                            const val = c.fmt === 'num3' ? fmt3(sum)
                                      : c.fmt === 'num1' ? fmt1(sum)
                                      : c.fmt === 'pct'  ? '' // no pct total
                                      : fmt2(sum);
                            return <td key={c.key} style={{ ...tdFoot, textAlign: 'right', fontFamily: '"Courier New", monospace' }}>{val}</td>;
                          }
                          return <td key={c.key} style={tdFoot} />;
                        })}
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </>
          )}
        </div>

      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
      `}</style>
    </div>
  );
};

// ── Small helpers ─────────────────────────────────────────
const lbl = { display:'block', fontSize:'0.78rem', fontWeight:'600', color:'#374151', marginBottom:'4px' };
const inp = {
  width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
  borderRadius: '6px', fontSize: '0.85rem', background: '#fff',
  boxSizing: 'border-box'
};
const spinner = {
  width: '16px', height: '16px',
  border: '2px solid rgba(255,255,255,0.3)',
  borderTopColor: '#fff', borderRadius: '50%',
  animation: 'spin .7s linear infinite',
  display: 'inline-block'
};
const actionBtn = (bg) => ({
  display: 'flex', alignItems: 'center', gap: '5px',
  padding: '7px 12px', border: '1px solid #d1d5db',
  borderRadius: '7px', background: '#fff', color: bg,
  cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600'
});
const tdFoot = {
  padding: '8px 10px', borderTop: '2px solid #d1d5db',
  fontSize: '0.8rem', color: '#1f2937'
};

const StatBadge = ({ label, value, color }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
    <span style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>
      {label}
    </span>
    <span style={{ fontSize: '1rem', fontWeight: '800', color, fontFamily: '"Courier New", monospace' }}>
      {value}
    </span>
  </div>
);

export default ConsignmentReports;